import { useRef } from "react"
import { twMerge } from "tailwind-merge"
import { CalendarState } from "react-stately"
import { mergeProps, useCalendarCell, useFocusRing } from "react-aria"
import { CalendarDate, isSameDay, isSameMonth, today } from "@internationalized/date"

interface DatePickerCalendarDayProps {
  date: CalendarDate
  timeZone: string
  calendarState: CalendarState
  startOfCurrentMonth: CalendarDate
}

export function DatePickerCalendarDay({
  date,
  timeZone,
  calendarState,
  startOfCurrentMonth,
}: DatePickerCalendarDayProps) {
  const dayElementRef = useRef<HTMLButtonElement>(null)
  const { focusProps, isFocused, isFocusVisible } = useFocusRing()

  const { cellProps, buttonProps, isSelected, isDisabled, formattedDate, isUnavailable } =
    useCalendarCell({ date }, calendarState, dayElementRef)

  const isToday = isSameDay(today(timeZone), date)
  const isOutsideCurrentMonth = !isSameMonth(startOfCurrentMonth, date)

  return (
    <td
      aria-current={isToday ? "date" : undefined}
      // react aria has a distinct interpretation for the disabled and unavailable dates.
      // For our implementation, we want to make both of them behaves the same way.
      // This ensures that whole months are still displayed even if all of its dates are unavailable.
      aria-disabled={isDisabled || isUnavailable ? true : undefined}
      {...cellProps}
    >
      <button
        aria-disabled={isDisabled || isUnavailable ? true : undefined}
        {...mergeProps(buttonProps, focusProps)}
        ref={dayElementRef}
        className={twMerge(
          "mx-1 my-0.5 flex h-7 w-7 items-center justify-center rounded bg-white hover:bg-primary-lighten-100 hover:text-primary-base",
          isOutsideCurrentMonth && "hidden",
          isToday && "border border-primary-base text-primary-base",
          (isDisabled || isUnavailable) && "pointer-events-none bg-gray-100 text-gray-300",
          isSelected && "bg-primary-base text-white hover:bg-primary-base hover:text-white",
          isFocused && "outline-none",
          isFocusVisible && "ring-primary-lighten-50 focus-within:ring-4 focus-visible:ring-4",
        )}
      >
        {formattedDate}
      </button>
    </td>
  )
}
